import React, { useState, useEffect, lazy, Suspense } from 'react';
import { useLanguage } from '@/hooks/use-language';
import { SEO } from '@/components/ui/SEO';
import {
  getCachedPortfolio, fetchPortfolio, getCachedHomeData, fetchHomeData,
  subscribeToCache, getCachedHero, fetchHeroTexts, getCachedHomeCta,
  saveHomeCta, getCachedHeroMedia
} from '@/lib/globalDataCache';
import { PortfolioProject } from '@/lib/portfolio-data';
import { getHomeHeroData, HomeHeroData } from '@/lib/cms-hero';
import { onCmsUpdate } from '@/lib/cms';
import { HomePageData, HOME_DEFAULTS } from '@/lib/homeContent';
import { buildOrganization, buildBreadcrumbs } from '@/lib/schema';
import { getPagePath, type Lang } from '@/lib/localizedRoutes';

// Above-the-fold — eagerly imported so hero/stats render without an extra
// roundtrip (these are part of the LCP critical path).
import { HeroSection } from '@/components/home/HeroSection';
import { StatsSection } from '@/components/home/StatsSection';

// Below-the-fold — lazy-loaded to shrink the home-page critical bundle.
// Each chunk is fetched in parallel after the main shell paints; the
// <Suspense fallback> reserves vertical space so we don't trigger layout shift.
const TechnologicalExcellence = lazy(() =>
  import('@/components/product/TechnologicalExcellence').then((m) => ({ default: m.TechnologicalExcellence }))
);
const Collections = lazy(() =>
  import('@/components/home/Collections').then((m) => ({ default: m.Collections }))
);
const FeaturedProjects = lazy(() =>
  import('@/components/home/FeaturedProjects').then((m) => ({ default: m.FeaturedProjects }))
);
const DealerCTA = lazy(() =>
  import('@/components/home/DealerCTA').then((m) => ({ default: m.DealerCTA }))
);

const BelowFoldFallback: React.FC<{ minH?: string }> = ({ minH = '400px' }) => (
  <div style={{ minHeight: minH }} aria-hidden />
);

export const Home: React.FC<{ seoEnabled?: boolean }> = ({ seoEnabled = true }) => {
  const { language } = useLanguage();
  const [activeProjectId, setActiveProjectId] = useState<string | null>(null);
  const [featuredProjects, setFeaturedProjects] = useState<PortfolioProject[]>([]);
  const [homeData, setHomeData] = useState<HomePageData | null>(null);
  const [, setHomeDataReady] = useState(false);
  const [heroLoading, setHeroLoading] = useState(true);

  const [heroData, setHeroData] = useState<HomeHeroData>({
    eyebrow: '',
    title: '',
    subtitle: '',
    cta1Text: '',
    cta1Url: `/${language}/catalog`,
    cta2Text: '',
    cta2Url: getPagePath('about', language as Lang),
    media: [],
  });
  const [heroMedia, setHeroMedia] = useState<HomeHeroData['media']>([]);

  useEffect(() => {
    // Start with empty to avoid stale flash, then fetch
    fetchPortfolio()
      .then((data) => setFeaturedProjects(data.slice(0, 4)))
      .catch(() => setFeaturedProjects([]));
  }, []);

  useEffect(() => {
    // Synchronously promote cached data for the new language so language
    // switching doesn't briefly show stale (previous-language) content.
    const cached = getCachedHomeData(language);
    if (cached) { setHomeData(cached); setHomeDataReady(true); }

    fetchHomeData(language)
      .then(data => { setHomeData(data); setHomeDataReady(true); })
      .catch(() => { setHomeData(HOME_DEFAULTS); setHomeDataReady(true); });

    return subscribeToCache(() => {
      const updated = getCachedHomeData(language);
      if (updated) { setHomeData(updated); setHomeDataReady(true); }
    });
  }, [language]);

  useEffect(() => {
    let cancelled = false;

    // Synchronously promote cached hero texts for the new language.
    const cachedHero = getCachedHero('home', language);
    if (cachedHero) {
      setHeroData(prev => ({
        ...prev,
        eyebrow: cachedHero.eyebrow || prev.eyebrow,
        title: cachedHero.title || prev.title,
        subtitle: cachedHero.subtitle || prev.subtitle,
      }));
    }
    const cachedCta = getCachedHomeCta(language);
    if (cachedCta) {
      setHeroData(prev => ({
        ...prev,
        cta1Text: cachedCta.cta1Text || prev.cta1Text,
        cta1Url: cachedCta.cta1Url || prev.cta1Url,
        cta2Text: cachedCta.cta2Text || prev.cta2Text,
        cta2Url: cachedCta.cta2Url || prev.cta2Url,
      }));
    }
    const cachedMedia = getCachedHeroMedia();
    if (cachedMedia && cachedMedia.length > 0) {
      setHeroMedia(cachedMedia);
      setHeroLoading(false);
    }

    // Fetch fresh texts and media
    fetchHeroTexts('home', language).then((texts) => {
      if (cancelled) return;
      setHeroData(prev => ({
        ...prev,
        eyebrow: texts.eyebrow || prev.eyebrow,
        title: texts.title || prev.title,
        subtitle: texts.subtitle || prev.subtitle,
      }));
    }).catch(() => { });

    getHomeHeroData(language, { cta1Url: `/${language}/catalog`, cta2Url: getPagePath('about', language as Lang) })
      .then((data) => {
        if (cancelled) return;
        saveHomeCta(language, {
          cta1Text: data.cta1Text,
          cta1Url: data.cta1Url || `/${language}/catalog`,
          cta2Text: data.cta2Text,
          cta2Url: data.cta2Url || getPagePath('about', language as Lang)
        });
        setHeroData(prev => ({
          ...prev,
          cta1Text: data.cta1Text,
          cta1Url: data.cta1Url || prev.cta1Url,
          cta2Text: data.cta2Text,
          cta2Url: data.cta2Url || prev.cta2Url,
        }));
        setHeroMedia(data.media);
        setHeroLoading(false);
      })
      .catch(() => {
        if (cancelled) return;
        setHeroLoading(false);
      });

    return () => { cancelled = true; };
  }, [language]);

  useEffect(() => {
    return onCmsUpdate((pageKey, blockKey, lang, value) => {
      if (pageKey !== 'home' || lang !== language) return;
      setHeroData((prev) => {
        const updated = { ...prev };
        if (blockKey === 'hero.eyebrow') updated.eyebrow = value;
        else if (blockKey === 'hero.title') updated.title = value;
        else if (blockKey === 'hero.subtitle') updated.subtitle = value;
        else if (blockKey === 'hero.cta1.text') updated.cta1Text = value;
        else if (blockKey === 'hero.cta1.url') updated.cta1Url = value;
        else if (blockKey === 'hero.cta2.text') updated.cta2Text = value;
        else if (blockKey === 'hero.cta2.url') updated.cta2Url = value;
        return updated;
      });
    });
  }, [language]);

  const about = homeData?.about ?? null;
  const stats = [
    { value: about?.stat1Value ?? '', label: about?.stat1Label ?? '' },
    { value: about?.stat2Value ?? '', label: about?.stat2Label ?? '' },
  ];

  return (
    <div className="overflow-hidden">
      <SEO
        enabled={seoEnabled}
        title={language === 'uz' ? "SARGO | SPC laminat, SPC panellar va gilam plitka ishlab chiqaruvchisi" : language === 'en' ? 'SARGO | SPC Laminate, SPC Panels and Carpet Tile Manufacturer' : 'SARGO | Производитель SPC-ламината, SPC-панелей и ковровой плитки'}
        description={language === 'uz'
          ? "SARGO — O'zbekistondan SPC laminat, SPC panellar va gilam plitka ishlab chiqaruvchisi. Zamonaviy ishlab chiqarish, yuqori sifat, turar-joy va tijorat xonalari uchun yechimlar."
          : language === 'en'
            ? 'SARGO — manufacturer of SPC laminate, SPC wall panels and carpet tile from Uzbekistan. Modern production, high quality and solutions for residential and commercial spaces.'
            : 'SARGO — производитель SPC-ламината, SPC-панелей и ковровой плитки из Узбекистана. Современное производство, высокое качество и решения для жилых и коммерческих помещений.'}
        canonical={`/${language}`}
        schema={[buildOrganization(window.location.origin), buildBreadcrumbs(window.location.origin, [{ name: 'SARGO', url: `/${language}` }])]}
      />
      <style dangerouslySetInnerHTML={{ __html: `@media (min-width: 1025px) { .hero-cta-button { border-radius: 0px !important; clip-path: none !important; } }` }} />

      <HeroSection heroData={heroData} heroMedia={heroMedia} loading={heroLoading} />

      <StatsSection
        aboutTitle={about?.title ?? ''}
        aboutDesc={about?.description ?? ''}
        stats={stats}
        aboutBtnUrl={about?.buttonUrl || getPagePath('about', language as Lang)}
        aboutBtnText={about?.buttonText ?? ''}
        aboutImgUrl={about?.imageUrl ?? ''}
        aboutQuote={about?.quote ?? ''}
        aboutQuoteAuthor={about?.quoteAuthor ?? ''}
      />

      <Suspense fallback={<BelowFoldFallback minH="600px" />}>
        <TechnologicalExcellence />
      </Suspense>

      <Suspense fallback={<BelowFoldFallback minH="500px" />}>
        <Collections dbData={homeData?.collections} />
      </Suspense>

      <Suspense fallback={<BelowFoldFallback minH="500px" />}>
        <FeaturedProjects
          projectsTitle={homeData?.projects.title ?? ''}
          projectsSubtitle={homeData?.projects.subtitle ?? ''}
          projectsBtnUrl={homeData?.projects.buttonUrl || `/${language}/portfolio`}
          projectsBtnText={homeData?.projects.buttonText ?? ''}
          featuredProjects={featuredProjects}
          activeProjectId={activeProjectId}
          setActiveProjectId={setActiveProjectId}
        />
      </Suspense>

      <Suspense fallback={<BelowFoldFallback minH="300px" />}>
        <DealerCTA
          dealerTitle={homeData?.dealer.title ?? ''}
          dealerDesc={homeData?.dealer.description ?? ''}
          dealerBtnUrl={homeData?.dealer.buttonUrl || getPagePath('become-dealer', language as Lang)}
          dealerBtnText={homeData?.dealer.buttonText ?? ''}
        />
      </Suspense>
    </div>
  );
};